export const DJ_VIDEO_BUCKET = 'product-videos';
export const DJ_VIDEO_MAX_BYTES = 80 * 1024 * 1024;
export const DJ_VIDEO_MAX_COUNT = 6;
export const DJ_VIDEO_MAX_SIZE_LABEL = '80 MB';

export const DJ_VIDEO_ALLOWED_MIME = ['video/mp4', 'video/webm', 'video/quicktime'] as const;
export const DJ_VIDEO_ALLOWED_EXTENSIONS = ['mp4', 'webm', 'mov'] as const;

export type DjProfileVideo = {
  id: string;
  dj_profile_id: string;
  storage_path: string;
  original_filename: string | null;
  mime_type: string | null;
  file_size_bytes: number | null;
  sort_order: number;
  created_at: string;
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const MIME_BY_EXTENSION: Record<(typeof DJ_VIDEO_ALLOWED_EXTENSIONS)[number], (typeof DJ_VIDEO_ALLOWED_MIME)[number]> = {
  mp4: 'video/mp4',
  webm: 'video/webm',
  mov: 'video/quicktime',
};

export function isDjProfileId(value: string): boolean {
  return UUID_RE.test(value.trim());
}

export function extensionFromFilename(filename: string): string {
  const parts = filename.toLowerCase().split('.');
  if (parts.length < 2) return '';
  return parts.pop() || '';
}

export function mimeFromFilename(filename: string): (typeof DJ_VIDEO_ALLOWED_MIME)[number] | null {
  const ext = extensionFromFilename(filename);
  if (ext === 'mp4' || ext === 'webm' || ext === 'mov') return MIME_BY_EXTENSION[ext];
  return null;
}

export function normalizeDjVideoMime(file: { name: string; type: string }): (typeof DJ_VIDEO_ALLOWED_MIME)[number] | null {
  const type = (file.type || '').toLowerCase().trim();
  if ((DJ_VIDEO_ALLOWED_MIME as readonly string[]).includes(type)) {
    return type as (typeof DJ_VIDEO_ALLOWED_MIME)[number];
  }
  return mimeFromFilename(file.name);
}

export type DjVideoValidationResult =
  | { ok: true; mimeType: (typeof DJ_VIDEO_ALLOWED_MIME)[number]; extension: string }
  | { ok: false; error: string };

export function validateDjVideoFile(file: { name: string; type: string; size: number }): DjVideoValidationResult {
  const mimeType = normalizeDjVideoMime(file);
  const extension = extensionFromFilename(file.name);
  if (!mimeType || !(DJ_VIDEO_ALLOWED_EXTENSIONS as readonly string[]).includes(extension)) {
    return { ok: false, error: 'Ogiltig filtyp. Använd MP4, WebM eller MOV.' };
  }
  if (!Number.isFinite(file.size) || file.size < 1) {
    return { ok: false, error: 'Filen är tom eller kunde inte läsas.' };
  }
  if (file.size > DJ_VIDEO_MAX_BYTES) {
    return { ok: false, error: `Filen är för stor. Maxstorlek är ${DJ_VIDEO_MAX_SIZE_LABEL}.` };
  }
  return { ok: true, mimeType, extension };
}

export function canManageDjVideos(sessionProfileId: string | null | undefined, targetProfileId: string): boolean {
  if (!sessionProfileId || !targetProfileId) return false;
  return sessionProfileId === targetProfileId && isDjProfileId(sessionProfileId);
}

/** Source of truth for ownership: always the inloggade DJs profil, aldrig ett klientskickat id. */
export function ownedDjProfileId(sessionProfileId: string | null | undefined): string | null {
  if (!sessionProfileId || !isDjProfileId(sessionProfileId)) return null;
  return sessionProfileId;
}

export function isOwnDjVideoStoragePath(storagePath: string, djProfileId: string): boolean {
  if (!isDjProfileId(djProfileId)) return false;
  const parts = storagePath.split('/');
  if (parts.length !== 3) return false;
  if (parts[0] !== 'dj-profiles') return false;
  if (parts[1] !== djProfileId) return false;
  if (!parts[2] || parts[2].includes('..')) return false;
  const ext = extensionFromFilename(parts[2]);
  return (DJ_VIDEO_ALLOWED_EXTENSIONS as readonly string[]).includes(ext);
}

export function buildDjVideoStoragePath(
  sessionProfileId: string,
  filename: string,
  uniquePart: string,
): string {
  const ownerId = ownedDjProfileId(sessionProfileId);
  if (!ownerId) {
    throw new Error('Ingen giltig DJ-profil i sessionen');
  }
  const validated = validateDjVideoFile({ name: filename, type: '', size: 1 });
  const extension = validated.ok ? validated.extension : extensionFromFilename(filename);
  if (!(DJ_VIDEO_ALLOWED_EXTENSIONS as readonly string[]).includes(extension)) {
    throw new Error('Ogiltig filtyp. Använd MP4, WebM eller MOV.');
  }
  const safeUnique = uniquePart.replace(/[^a-zA-Z0-9-]/g, '');
  if (!safeUnique) {
    throw new Error('Kunde inte skapa filnamn');
  }
  return `dj-profiles/${ownerId}/${safeUnique}.${extension}`;
}

/** Sibling JPEG next to the video file: dj-profiles/{id}/{name}.jpg */
export function posterStoragePathForVideo(storagePath: string): string | null {
  const parts = storagePath.split('/');
  if (parts.length !== 3) return null;
  if (parts[0] !== 'dj-profiles') return null;
  if (!isDjProfileId(parts[1])) return null;
  if (!parts[2] || parts[2].includes('..')) return null;
  const ext = extensionFromFilename(parts[2]);
  if (!(DJ_VIDEO_ALLOWED_EXTENSIONS as readonly string[]).includes(ext)) return null;
  const base = parts[2].slice(0, parts[2].length - ext.length - 1);
  if (!base) return null;
  return `dj-profiles/${parts[1]}/${base}.jpg`;
}

export async function captureDjVideoPosterBlob(file: File): Promise<Blob | null> {
  if (typeof document === 'undefined') return null;
  const objectUrl = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.muted = true;
  video.defaultMuted = true;
  video.playsInline = true;
  video.setAttribute('playsinline', 'true');
  video.preload = 'auto';
  video.src = objectUrl;

  const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

  try {
    await Promise.race([
      new Promise<void>((resolve, reject) => {
        video.addEventListener('loadeddata', () => resolve(), { once: true });
        video.addEventListener('error', () => reject(new Error('load')), { once: true });
      }),
      wait(8000).then(() => {
        throw new Error('timeout');
      }),
    ]);

    if (!video.videoWidth || !video.videoHeight) return null;

    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    const seekTo = duration > 0 ? Math.min(1, Math.max(0.1, duration * 0.08)) : 0.1;
    await Promise.race([
      new Promise<void>((resolve) => {
        video.addEventListener('seeked', () => resolve(), { once: true });
        video.currentTime = seekTo;
      }),
      wait(1500),
    ]);

    const maxWidth = 720;
    const scale = video.videoWidth > maxWidth ? maxWidth / video.videoWidth : 1;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
    canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((next) => resolve(next), 'image/jpeg', 0.82);
    });
    return blob && blob.size > 0 ? blob : null;
  } catch {
    return null;
  } finally {
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(objectUrl);
  }
}

export function moveVideoId(orderedIds: string[], id: string, direction: 'up' | 'down'): string[] {
  const index = orderedIds.indexOf(id);
  if (index < 0) return orderedIds;
  const target = direction === 'up' ? index - 1 : index + 1;
  if (target < 0 || target >= orderedIds.length) return orderedIds;
  const next = [...orderedIds];
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
}

export function sortDjProfileVideos<T extends { sort_order: number; created_at: string }>(videos: T[]): T[] {
  return [...videos].sort((a, b) => {
    if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
    return a.created_at.localeCompare(b.created_at);
  });
}
